<template>
  <BaseModal :modalTitle="$t('W: Opret liste og beskrivelse')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('W: Navn')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <div class="mb-2 flex items-center gap-2">
        <AppSwitch
          :isActive="form.usesDescriptions"
          :title="$t('W: Bruger beskrivelser')"
          @on-toggle="form.usesDescriptions = !form.usesDescriptions" />
        <span>{{ $t('W: Bruger beskrivelser') }}</span>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

function createForm() {
  return {
    name: '',
    usesDescriptions: true,
  }
}

export default {
  name: 'CreateReportDescriptionModal',

  emits: ['close'],

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
  },

  methods: {
    ...mapActions(useReportDescriptionStore, ['createReportDescription']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.createReportDescription({
          name: this.form.name,
          usesDescriptions: this.form.usesDescriptions,
        })
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Liste og beskrivelse oprettet'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette liste og beskrivelse'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
